import {
  IImage,
  IBaseGetRequest,
  IBaseContract,
  IEmailPreviewImage,
  ICampaign,
  IBuyerIdentity,
  CryptoCurrenciesE,
} from 'primsell-shared-layer/types'

export enum RewardTypeE {
  DISCOUNT = 'discount',
  EVENTS = 'event',
  FREE_PRODUCT = 'free_product',
  EXCLUSIVE_ACCESS = 'exclusive_access',
}

export enum RewardStatusE {
  DRAFT = 'draft', //Draft
  PENDING = 'pending', //New
  ACTIVE = 'active', //Drop in Progress
  COMPLETED = 'completed', //Expired
}

export interface IRewardMeta {
  additionalDetails: string | null
  eventDate: string | null
  eventLocation: string | null
  eventLocationTimezoneOffset: number | null
  eventLocationPlaceId: string | null
}

export interface IBaseReward {
  id: string
  name: string | null
  description: string | null
  type: RewardTypeE | null
  status: RewardStatusE | null
  isDraft: boolean
  tokenId: string | null
  contract: IBaseContract | null
  amount: number | null
  freezeAmount: number | null
  dropAmount: number | null
  availableAmount: number | null
  hasDrops: boolean | null
  images: IImage[] | null
  campaignId: string | null
  campaign?: Pick<ICampaign, 'id' | 'name' | 'codeType'> | null
  createdAt: string | null
  updatedAt: string | null
}

export interface IFullReward extends IBaseReward {
  campaignId: string | null
  startDate: string | null
  endDate: string | null
  redeemExpiredDate: string | null
  senderEmail: string | null
  identityId: string | null
  imageId: string | null
  redeemEmailTemplateId: string | null
  dropEmailTemplateId: string | null
  redeemEmailTemplate?: IEmailPreviewImage | null
  dropEmailTemplate?: IEmailPreviewImage | null
  meta: IRewardMeta | null
  redeemCodes?: string[] | null
  additionalRedeemCodes?: string[] | null
  uploadedRedeemCodesCount: number | null
  redeemAmount: number | null
}

export type GetRewardsFilterPropertiesT = 'type' | 'startDate' | 'campaign.id'
export type GetRewardsSortPropertiesT = 'name' | 'startDate' | 'createdAt'

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface IGetRewardsRequest extends IBaseGetRequest<GetRewardsFilterPropertiesT, GetRewardsSortPropertiesT> {}

export type IGetRewardsListFilterProperties = 'createdAt' | 'campaign.id'

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface IRequestGetFilteredRewardsIdNameList extends IBaseGetRequest<IGetRewardsListFilterProperties> {}

export interface IResponseGetFilteredRewardsIdNameList {
  id: IBaseReward['id']
  name: IBaseReward['name']
}

export interface IResponseGetRewardMetadata {
  name: IBaseReward['name']
  description: IBaseReward['description']
  image: string | null
}

export interface IResponseGenerateRewardTokenId {
  tokenId: string
}

export interface ICreateRedeemReward {
  tokenId: IFullReward['tokenId']
  campaignId: IFullReward['campaignId']
  name: IFullReward['name']
  description: IFullReward['description']
  amount: IFullReward['amount']
  startDate: IFullReward['startDate']
  endDate: IFullReward['endDate']
  redeemExpiredDate: IFullReward['redeemExpiredDate']
  senderEmail: IFullReward['senderEmail']
  imageId: IFullReward['imageId']
  isDraft: IFullReward['isDraft']
  dropEmailTemplateId: IFullReward['dropEmailTemplateId']
  redeemEmailTemplateId: IFullReward['redeemEmailTemplateId']
  redeemCodes?: IFullReward['redeemCodes']
  additionalRedeemCodes?: IFullReward['uploadedRedeemCodesCount']
}

export interface ICreateEventReward {
  tokenId: IFullReward['tokenId']
  campaignId: IFullReward['campaignId']
  name: IFullReward['name']
  description: IFullReward['description']
  amount: IFullReward['amount']
  startDate: IFullReward['startDate']
  endDate: IFullReward['endDate']
  redeemExpiredDate: IFullReward['redeemExpiredDate']
  senderEmail: IFullReward['senderEmail']
  imageId: IFullReward['imageId']
  isDraft: IFullReward['isDraft']
  dropEmailTemplateId: IFullReward['dropEmailTemplateId']
  redeemEmailTemplateId: IFullReward['redeemEmailTemplateId']
  meta: IFullReward['meta']
}

export interface IUpdateRedeemReward extends Omit<ICreateRedeemReward, 'tokenId'> {
  id: IFullReward['id']
}

export interface IUpdateEventReward extends Omit<ICreateEventReward, 'tokenId'> {
  id: IFullReward['id']
}

export interface IRequestRedeemReward {
  code: string
  email: string
  firstName: string
  lastName: string
}

export interface IPublicReward extends IBaseReward {
  contract: IBaseContract | null
  startDate: string | null
  endDate: string | null
  redeemExpiredDate: string | null
}

export interface IRequestPublicRewardByCode {
  code: string
}

export interface IRequestRedeemReward {
  code: string
  email: string
  firstName: string
  lastName: string
}

export enum RewardDistributionStatusE {
  PENDING = 'pending',
  DECLINED = 'declined',
  PROCESSING = 'processing',
  COMPLETED = 'completed',
}

export interface IBaseRewardDistribution {
  id: string | null
  amount: number | null
  tx: string | null
  status: RewardDistributionStatusE | null
  createdAt: string
  updatedAt: string
}

export type IGetRewardsDropsFilterProperties = 'reward.id' | 'campaign.name'
export type IGetRewardsDropsSortProperties = 'createdAt'

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface IRequestGetRewardsDropsList
  extends IBaseGetRequest<IGetRewardsDropsFilterProperties, IGetRewardsDropsSortProperties> {}

export interface IRewardDropData extends IBaseRewardDistribution {
  buyerIdentity: IBuyerIdentity | null
}

export type IGetRewardsSalesFilterProperties = 'reward.id' | 'campaign.name'
export type IGetRewardsSalesSortProperties = 'createdAt'

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface IRequestGetRewardsSalesList
  extends IBaseGetRequest<IGetRewardsSalesFilterProperties, IGetRewardsSalesSortProperties> {}

export interface IRewardSalesData extends IBaseRewardDistribution {
  buyerIdentity: IBuyerIdentity | null
  price: string | null
  royalty: string | null
  currency: CryptoCurrenciesE | null
  rewardId: string | null
}
